import React from 'react';

import { Alert, Box, Stack } from '@mui/material';

import { getOffersByTag, replaceOffersByTag } from '~/utils/societePayloadBuilder/offersHub/offers';
import type { OffersHubPayloadBuilderState } from '~/utils/societePayloadBuilder/offersHub/types';
import type { OffersHubValidationResult } from '~/utils/societePayloadBuilder/offersHub/validatePayload';

import LoadPayloadSection from '../LoadPayloadSection';
import { PAYLOAD_BUILDER_REQUIRED_FIELDS_SX } from '../FieldLabels';

import OffersHubHeroItemsSection from './OffersHubHeroItemsSection';
import OffersHubOffersSection from './OffersHubOffersSection';

interface OffersHubPayloadBuilderFormProps {
  collapseResetKey: number;
  state: OffersHubPayloadBuilderState;
  onChange: (next: OffersHubPayloadBuilderState) => void;
  validation: OffersHubValidationResult;
  loadJson: string;
  onLoadJsonChange: (value: string) => void;
  loadError: string | null;
  loadCollapseKey?: number;
  onLoadPayload: () => void;
  onClearLoad: () => void;
  onUploadFile: (file: File) => void;
}

export default function OffersHubPayloadBuilderForm({
  collapseResetKey,
  state,
  onChange,
  validation,
  loadJson,
  onLoadJsonChange,
  loadError,
  loadCollapseKey,
  onLoadPayload,
  onClearLoad,
  onUploadFile,
}: OffersHubPayloadBuilderFormProps) {
  const partnerOffers = getOffersByTag(state.offers, 'partner_offer');
  const luxuryEscapesOffers = getOffersByTag(state.offers, 'luxury_escapes_offer');

  const handlePartnerOffersChange = (offers: OffersHubPayloadBuilderState['offers']) => {
    onChange({ ...state, offers: replaceOffersByTag(state.offers, 'partner_offer', offers) });
  };

  const handleLuxuryEscapesOffersChange = (offers: OffersHubPayloadBuilderState['offers']) => {
    onChange({ ...state, offers: replaceOffersByTag(state.offers, 'luxury_escapes_offer', offers) });
  };

  return (
    <Stack spacing={2}>
      <LoadPayloadSection
        loadJson={loadJson}
        loadError={loadError}
        collapseKey={loadCollapseKey}
        onLoadJsonChange={onLoadJsonChange}
        onLoadPayload={onLoadPayload}
        onClearLoad={onClearLoad}
        onUploadFile={onUploadFile}
      />

      {!validation.isValid ? (
        <Alert severity="warning">
          <Box component="ul" sx={{ my: 0, pl: 2.5 }}>
            {validation.errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </Box>
        </Alert>
      ) : null}

      <Box key={collapseResetKey} sx={PAYLOAD_BUILDER_REQUIRED_FIELDS_SX}>
        <OffersHubHeroItemsSection
          items={state.heroCarouselItems}
          onChange={(heroCarouselItems) => onChange({ ...state, heroCarouselItems })}
        />
        <OffersHubOffersSection
          title="Partner offers"
          subtitle="Offers by partners with brand name and industry filter."
          addButtonLabel="Add partner offer"
          offerTag="partner_offer"
          offers={partnerOffers}
          onChange={handlePartnerOffersChange}
        />
        <OffersHubOffersSection
          title="Luxury Escapes offers"
          subtitle="Offers by LE."
          addButtonLabel="Add Luxury Escapes offer"
          offerTag="luxury_escapes_offer"
          offers={luxuryEscapesOffers}
          onChange={handleLuxuryEscapesOffersChange}
        />
      </Box>
    </Stack>
  );
}
